import { createContext, useState, useRef, useEffect, useMemo, useContext } from 'react'
import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD } from '@aytm/lib/common/assets'

import { DEFAULT_IMAGE_RATIO, DEFAULT_FULLSCREEN_IMAGE_RATIO } from '../config/CompareItems_const'
import {
  CompareItemsProps,
  CompareItemsImages,
  CompareItemsSelectedItem,
  CompareItemsItem,
  CompareItemSelectedImage
} from '../types'

export type CompareItemsImagesState = {
  showImages: boolean
  imageRatio: number
  fullScreenImageRatio: number
  getImageURL: CompareItemsProps['getImageURL']
}

export const ImagesContext = createContext<CompareItemsImages>(null)

export default function useImages(): CompareItemsImages {
  const images = useContext(ImagesContext)
  return images
}

export function useImagesState(
  props: CompareItemsProps,
  { fullScreen = false, selectedItems = [] }: { fullScreen: boolean; selectedItems: CompareItemsSelectedItem[] }
): CompareItemsImages {
  const [imagesState, setImagesState] = useState<CompareItemsImagesState>(() => {
    return {
      showImages: false === props.canUseImages ? false : null == props.showImages ? true : Boolean(props.showImages),
      imageRatio: null == props.imageRatio ? DEFAULT_IMAGE_RATIO : props.imageRatio,
      fullScreenImageRatio:
        null == props.fullScreenImageRatio ? DEFAULT_FULLSCREEN_IMAGE_RATIO : props.fullScreenImageRatio,
      getImageURL:
        null == props.getImageURL
          ? (path: string, width: number, height: number) =>
              path
                ? getImageAssetPath({
                    host: props.imagesDomain,
                    path,
                    filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
                    width: Math.round(width),
                    height: Math.round(height),
                    backgroundColor: 'white'
                  })
                : null
          : props.getImageURL
    }
  })

  // last props
  const lastProps = useRef<CompareItemsProps>()
  if (null == lastProps.current) {
    lastProps.current = { ...props }
  }

  // sync state
  useEffect(() => {
    let update: Partial<CompareItemsImagesState> = null

    if (lastProps.current.showImages !== props.showImages || lastProps.current.canUseImages !== props.canUseImages) {
      update = { ...(update || {}), showImages: false === props.canUseImages ? false : props.showImages }
      lastProps.current.showImages = props.showImages
      lastProps.current.canUseImages = props.canUseImages
    }
    if (lastProps.current.imageRatio !== props.imageRatio) {
      update = { ...(update || {}), imageRatio: props.imageRatio }
      lastProps.current.imageRatio = props.imageRatio
    }
    if (lastProps.current.fullScreenImageRatio !== props.fullScreenImageRatio) {
      update = { ...(update || {}), fullScreenImageRatio: props.fullScreenImageRatio }
      lastProps.current.fullScreenImageRatio = props.fullScreenImageRatio
    }
    if (lastProps.current.getImageURL !== props.getImageURL) {
      update = { ...(update || {}), getImageURL: props.getImageURL }
      lastProps.current.getImageURL = props.getImageURL
    }

    if (update) {
      setImagesState(prev => {
        return { ...prev, ...update }
      })
    }
  }, [props.canUseImages, props.showImages, props.imageRatio, props.fullScreenImageRatio, props.getImageURL])

  // callbacks
  const fnRef = useRef<{
    setShowImages: (showImages: boolean) => void
    onShowImagesUpdated: (showImages: boolean) => void
    onClickImage: {
      callback: (itemIndex: number, item: CompareItemsItem, items: CompareItemsItem[]) => void
      items: CompareItemsItem[]
    }
  }>()
  if (null == fnRef.current) {
    fnRef.current = {
      setShowImages: showImages => {
        setImagesState(prev => {
          return { ...prev, showImages: Boolean(showImages) }
        })
        fnRef.current.onShowImagesUpdated?.(Boolean(showImages))
      },
      onShowImagesUpdated: props.onShowImagesUpdated,
      onClickImage: {
        callback: props.onClickImage,
        items: props.items
      }
    }
  }
  fnRef.current.onShowImagesUpdated = props.onShowImagesUpdated
  fnRef.current.onClickImage.callback = props.onClickImage
  fnRef.current.onClickImage.items = props.items

  // selected items images
  const selectedImages: CompareItemSelectedImage[] = useMemo(
    () =>
      selectedItems.map(item => {
        const image = item?.image
        return {
          id: image?.id,
          path: image?.original?.path,
          alt: item?.text,
          height: image?.original?.height,
          width: image?.original?.width,
          _item: item
        }
      }),
    [selectedItems]
  )

  const images: CompareItemsImages = useMemo(() => {
    return {
      selectedImages,
      showImages: imagesState.showImages,
      setShowImages: fnRef.current.setShowImages,
      imageRatio: fullScreen ? imagesState.fullScreenImageRatio : imagesState.imageRatio,
      getImageURL: imagesState.getImageURL,
      get onClickImage() {
        return fnRef.current.onClickImage
      }
    }
  }, [selectedImages, imagesState, fullScreen])

  return images
}
